/*
 * InputSelect component is a generic select component to handle
 * filtering and selecting from a list of suggestions.

 * This component was taken from CustomerSelect in the react library.
 * And main difference is it is passes in a list prefetched records.
 * (TODO will move it to skeleton later)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SearchableDropdown, SelectOption } from '@paytrace/pt-skel/Component'

const SELECT_OPTION_LIMIT = 15

const SelectOptionTypes = {
  NONE: '-1',
  SUMMARY_INFO: '-2',
  LOADING: '-3',
}

export interface SelectProps {
  selectedItem?: string
  handleSelectionChange: (selected: SelectOption) => void
  className?: string
  fieldsetClassName?: string
  label?: string
  items?: string[] | SelectOption[]
  isLoading?: boolean
  placeHolder?: string
  filterMatcherFn?: (text: string, pattern: string) => boolean
}

function createInitialOptions (noneSelection: string) {
  return [
    { name: noneSelection, val: SelectOptionTypes.NONE },
    { name: 'Loading...', val: SelectOptionTypes.LOADING },
  ]
}

function normalizeItems (items: string[] | SelectOption[]): SelectOption[] {
  return items.map(item => {
    if (typeof item === 'string') {
      return { name: item, val: item }
    }
    return item
  })
}

function defaultFilterMatcherFn (text: string, pattern: string) {
  return text.toLowerCase().includes(pattern.toLowerCase())
}

export function InputSelect (props: SelectProps): React.ReactElement {
  const placeHolder = props.placeHolder || '-- Select --'
  const defaultOptions = createInitialOptions(placeHolder)
  const [options, setOptions] = useState<SelectOption[]>(defaultOptions)

  const normalizedItems = useMemo(() => {
    return props.items ? normalizeItems(props.items) : []
  }, [props.items])

  const getSelectedOption = (): SelectOption => {
    if (!props.selectedItem) {
      return { name: '', val: '' }
    }
    const matched = normalizedItems.find((item) => item.val === props.selectedItem)
    if (matched) {
      return matched
    }
    return { name: props.selectedItem, val: props.selectedItem }
  }

  const currentSelect = getSelectedOption()

  const updateFilteredSuggestions = useCallback((val: string) => {
    if (!props.items) return

    let filtered = normalizedItems
    if (val) {
      const matcher = props.filterMatcherFn || defaultFilterMatcherFn
      filtered = normalizedItems.filter((item) => matcher(item.name, val))
    }

    const filteredItems = filtered.slice(0, SELECT_OPTION_LIMIT)
    const options = filteredItems

    // create the final list of options with non selectable items
    const finalOptions = [defaultOptions[0], ...options]

    const totalCount = filtered.length
    if (totalCount > SELECT_OPTION_LIMIT) {
      finalOptions.push({
        name: `${SELECT_OPTION_LIMIT} of ${totalCount} shown. Type to refine.`,
        val: SelectOptionTypes.SUMMARY_INFO, // non-selectable summary info item
      })
    } else if (props.isLoading) {
      finalOptions.push(defaultOptions[1])
    }

    setOptions(finalOptions)
  }, [
    props.items,
    props.filterMatcherFn,
    props.isLoading,
    normalizedItems,
    defaultOptions,
  ])

  // Populate the suggestion list whenever the prefetched users list,
  // loading status, or typed value changes
  useEffect(() => {
    updateFilteredSuggestions(props.selectedItem || '')
  }, [updateFilteredSuggestions, props.selectedItem])

  function handleSearchChange (val: string) {
    props.handleSelectionChange({ name: val, val })
    updateFilteredSuggestions(val)
  }

  function handleSelectionChange (evt: any) {
    if ([
      SelectOptionTypes.NONE,
      SelectOptionTypes.SUMMARY_INFO,
      SelectOptionTypes.LOADING,
    ].includes(evt.val)) {
      // Ignore click on the select menu options
      // for empty row, summary info item or loading indicator
      return
    }
    props.handleSelectionChange(evt)
    // filter suggestions list when a user is selected
    updateFilteredSuggestions(evt.val)
  }

  return (
    <SearchableDropdown
      options={options}
      selectedValue={currentSelect}
      handleSearchChange={handleSearchChange}
      handleSelectionChange={handleSelectionChange}
      className={props.className + ' hide-label'}
      placeHolder={placeHolder}
      label={props?.label}
    />
  )
}
